import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/lib/content";
import { NAV } from "@/data/site";
import { useLang } from "@/lib/lang";
import AnimatedBlob from "./AnimatedBlob";
import PlacesLogo from "./PlacesLogo";
import { ArrowUpRight, MapPin } from "lucide-react";

export default function Footer() {
  const { t } = useLang();
  const { site: SITE, branding = {}, footer_config = {}, ecosystem = [] } = useContent();
  const labels = footer_config.labels || {};
  const tagline = footer_config.tagline_override || SITE.tagline;

  return (
    <footer className="theme-dark relative overflow-hidden grain" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Link to="/" className="font-display text-5xl md:text-6xl tracking-tighter leading-none flex items-center">
              {branding.logo_url_dark || branding.logo_url ? (
                <img src={branding.logo_url_dark || branding.logo_url} alt={branding.brand_name || "Places"} style={{ height: (branding.logo_height || 40) * 1.5 }} className="object-contain" />
              ) : (
                <PlacesLogo colorMode="dark" size="text-5xl md:text-6xl" />
              )}
            </Link>
            <p className="mt-6 text-neutral-400 max-w-md text-sm leading-relaxed">{tagline}. Branding e marketing especializados em empreendimentos imobiliários há mais de 30 anos.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contato?intent=client" className="bg-[#D4FF00] text-black px-5 py-3 font-semibold text-sm rounded-full hover:bg-white transition" data-testid="footer-cta-client">{t.cta.client}</Link>
              <Link to="/contato?intent=partner" className="border border-white/30 px-5 py-3 font-semibold text-sm rounded-full hover:bg-white hover:text-black transition" data-testid="footer-cta-partner">{t.cta.partner}</Link>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-widest text-neutral-500 mb-4">{labels.menu || t.footer.menu}</h4>
            <ul className="space-y-3 text-sm">
              {NAV.map((n) => <li key={n.key}><Link to={n.path} className="hover:text-accent-yellow transition" data-testid={`footer-nav-${n.key}`}>{t.nav[n.key]}</Link></li>)}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-widest text-neutral-500 mb-4">{labels.offices || t.footer.offices}</h4>
            <ul className="space-y-4 text-sm text-neutral-300">
              {(SITE.units || []).map((u) => (
                <li key={u.city} className="flex gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-accent-green flex-shrink-0" />
                  <div>
                    <div className="font-medium text-white">{u.city}/{u.state} {u.role !== "Unidade" && <span className="text-xs text-accent-yellow">— {u.role}</span>}</div>
                    <div className="text-xs text-neutral-500">{u.phone}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-widest text-neutral-500 mb-4">{labels.social || "Social"}</h4>
            <ul className="space-y-3 text-sm">
              {(SITE.socials || []).map((s) => (
                <li key={s.name}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-accent-yellow transition" data-testid={`footer-social-${s.name.toLowerCase()}`}>
                    {s.name} <ArrowUpRight className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
            <h4 className="text-xs uppercase tracking-widest text-neutral-500 mb-4 mt-8">{labels.ecosystem || t.footer.ecosystem}</h4>
            <div className="flex flex-wrap gap-2">
              {(ecosystem || []).map((b) => (
                b.url ? (
                  <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer" className="text-[10px] border border-white/20 px-2.5 py-1 rounded-full hover:bg-white/10">{b.name}</a>
                ) : (
                  <span key={b.name} className="text-[10px] border border-white/20 px-2.5 py-1 rounded-full">{b.name}</span>
                )
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-neutral-500">
          <span>© {new Date().getFullYear()} {branding.brand_name ? branding.brand_name.charAt(0).toUpperCase() + branding.brand_name.slice(1) : SITE.brand}. {labels.rights || t.footer.rights}.</span>
          <div className="flex gap-6">
            <Link to="/privacidade" className="hover:text-white transition" data-testid="footer-privacy">{labels.legal || t.footer.legal}</Link>
            {footer_config.copyright_extra && <span>{footer_config.copyright_extra}</span>}
          </div>
        </div>
      </div>

      <AnimatedBlob className="-bottom-32 -right-32 w-[500px] h-[500px] bg-accent-orange opacity-20" />
    </footer>
  );
}
